import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Modal from '../../components/UI/Modal'
import { apiService } from '../../services/api'

const Queues = () => {
	const [queues, setQueues] = useState([])
	const [connections, setConnections] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [showModal, setShowModal] = useState(false)
	const [editingQueue, setEditingQueue] = useState(null)
	const [queueForm, setQueueForm] = useState({
		name: '',
		connectionId: '',
		description: '',
		systemResult: true,
	})
	const [runningQueues, setRunningQueues] = useState(new Set())

	useEffect(() => {
		loadData()
	}, [])

	const loadData = async () => {
		try {
			setLoading(true)
			setError('')

			// Загружаем подключения для выпадающего списка
			const connectionsData = await apiService.getConnections()
			console.log('Loaded connections:', connectionsData)
			setConnections(connectionsData)

			// Загружаем цепочки (это наши очереди)
			console.log('Loading queues (chains)...')
			const data = await apiService.getChains()
			console.log('Loaded queues (chains) from API:', data)

			// Логируем каждую очередь для отладки
			if (data && Array.isArray(data)) {
				data.forEach((queue, index) => {
					console.log(`Queue [${index}]:`, {
						id: queue.id,
						name: queue.name,
						connectionId: queue.connectionId,
						hasId: !!queue.id,
						hasName: !!queue.name,
						rawData: queue, // весь объект для отладки
					})
				})
			} else {
				console.log('Queues data is not an array:', data)
			}

			setQueues(data || [])
		} catch (err) {
			console.error('Failed to load data:', err)

			let errorMessage = 'Ошибка при загрузке очередей'

			if (err.message.includes('404')) {
				errorMessage = 'API для очередей не найден. Проверьте эндпоинт /chains'
			} else if (err.message.includes('401') || err.message.includes('403')) {
				errorMessage = 'Ошибка авторизации. Проверьте API ключ.'
			}

			setError(errorMessage)

			// Тестовые данные для разработки
			setQueues([
				{
					id: 1,
					name: 'datamart_1',
					connectionId: 1,
					description: 'Тестовая очередь 1',
					status: 'stopped',
				},
				{
					id: 2,
					name: 'datamart_2',
					connectionId: 1,
					description: 'Тестовая очередь 2',
					status: 'stopped',
				},
			])
		} finally {
			setLoading(false)
		}
	}

	const handleAddQueue = () => {
		setEditingQueue(null)
		setQueueForm({
			name: '',
			connectionId: connections.length > 0 ? connections[0].id : '',
			description: '',
			systemResult: true,
		})
		setShowModal(true)
	}

	const handleEditQueue = queue => {
		setEditingQueue(queue)
		setQueueForm({
			name: queue.name,
			connectionId: queue.connectionId,
			description: queue.description || '',
			systemResult: queue.systemResult || true,
		})
		setShowModal(true)
	}

	const handleSaveQueue = async e => {
		e.preventDefault()

		try {
			setError('')

			if (editingQueue) {
				await apiService.updateChain(editingQueue.id, queueForm)
			} else {
				await apiService.createChain(queueForm)
			}

			await loadData()
			setShowModal(false)
			setEditingQueue(null)
		} catch (err) {
			setError('Ошибка при сохранении очереди')
			console.error('Failed to save queue:', err)
		}
	}

	const handleRunQueue = async queueId => {
		if (!queueId || isNaN(parseInt(queueId))) {
			console.error('Invalid queueId for running:', queueId)
			alert('Неверный ID очереди')
			return
		}

		if (runningQueues.has(queueId)) return

		try {
			setRunningQueues(prev => new Set(prev).add(queueId))

			await apiService.runChain(queueId)

			// Обновляем статус в локальном состоянии
			setQueues(prevQueues =>
				prevQueues.map(queue =>
					queue.id == queueId // Нестрогое сравнение для строк/чисел
						? { ...queue, status: 'running' }
						: queue,
				),
			)

			// Сбрасываем статус через 5 секунд
			setTimeout(() => {
				setQueues(prevQueues =>
					prevQueues.map(queue =>
						queue.id == queueId ? { ...queue, status: 'stopped' } : queue,
					),
				)
				setRunningQueues(prev => {
					const newSet = new Set(prev)
					newSet.delete(queueId)
					return newSet
				})
			}, 5000)
		} catch (err) {
			console.error('Failed to run queue:', err)
			setRunningQueues(prev => {
				const newSet = new Set(prev)
				newSet.delete(queueId)
				return newSet
			})
		}
	}

	const handleDeleteQueue = async id => {
		if (!id || isNaN(parseInt(id))) {
			console.error('Invalid queueId for deletion:', id)
			alert('Неверный ID очереди')
			return
		}

		if (
			!window.confirm(
				'Вы уверены, что хотите удалить эту очередь? Все запросы в ней также будут удалены.',
			)
		) {
			return
		}

		try {
			await apiService.deleteChain(id)
			await loadData()
		} catch (err) {
			setError('Ошибка при удалении очереди')
			console.error('Failed to delete queue:', err)
		}
	}

	const getConnectionName = connectionId => {
		try {
			// Проверяем входные данные
			if (connectionId === undefined || connectionId === null) {
				return 'Не указано'
			}

			// Проверяем, что connections - массив
			if (!Array.isArray(connections)) {
				console.warn('connections is not an array:', connections)
				return `ID: ${connectionId}`
			}

			// Ищем подключение
			for (const conn of connections) {
				if (!conn) continue

				const connId = conn.id
				if (connId === undefined || connId === null) continue

				// Преобразуем в строки для сравнения
				if (String(connId) === String(connectionId)) {
					const name = conn.name || 'Без названия'
					const dbType = conn.db_type ? ` (${conn.db_type})` : ''
					return name + dbType
				}
			}

			return `ID: ${connectionId}`
		} catch (error) {
			console.error('Critical error in getConnectionName:', error)
			return 'Ошибка загрузки'
		}
	}

	if (loading) {
		return (
			<div>
				<h2>Очереди ETL</h2>
				<div
					className='loading'
					style={{
						padding: '2rem',
						textAlign: 'center',
						color: '#666',
					}}
				>
					Загрузка очередей...
				</div>
			</div>
		)
	}

	return (
		<div>
			<h2>Очереди ETL</h2>

			{error && (
				<div
					className='error-message'
					style={{
						backgroundColor: '#fff5f5',
						border: '1px solid #feb2b2',
						color: '#c53030',
						padding: '1rem',
						borderRadius: '8px',
						marginBottom: '1.5rem',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<div>
						<strong>Внимание:</strong> {error}
					</div>
					<button
						onClick={loadData}
						className='button button-secondary'
						style={{
							marginLeft: '1rem',
							whiteSpace: 'nowrap',
						}}
					>
						Повторить
					</button>
				</div>
			)}

			<div
				className='table'
				style={{
					width: '100%',
					overflowX: 'auto',
					border: '1px solid #e0e0e0',
					borderRadius: '8px',
					marginBottom: '1.5rem',
				}}
			>
				<div
					className='table__row table__row_header'
					style={{
						display: 'grid',
						gridTemplateColumns:
							'minmax(250px, 2fr) minmax(150px, 1fr) minmax(150px, 1fr) minmax(250px, 1.5fr)',
						backgroundColor: '#f5f5f5',
						fontWeight: '600',
					}}
				>
					<div style={{ padding: '12px 16px' }}>Название</div>
					<div style={{ padding: '12px 16px' }}>Подключение</div>
					<div style={{ padding: '12px 16px' }}>Статус</div>
					<div style={{ padding: '12px 16px' }}>Действия</div>
				</div>

				{queues.length === 0 ? (
					<div
						className='table__row'
						style={{
							display: 'grid',
							gridTemplateColumns: '1fr',
						}}
					>
						<div
							style={{
								padding: '24px 16px',
								textAlign: 'center',
								color: '#666',
							}}
						>
							Нет очередей. Добавьте первую очередь.
						</div>
					</div>
				) : (
					queues.map(queue => {
						const isRunning =
							runningQueues.has(queue.id) || queue.status === 'running'

						return (
							<div
								key={queue.id || `queue-${Math.random()}`}
								className='table__row'
								style={{
									display: 'grid',
									gridTemplateColumns:
										'minmax(250px, 2fr) minmax(150px, 1fr) minmax(150px, 1fr) minmax(250px, 1.5fr)',
									borderTop: '1px solid #f0f0f0',
									alignItems: 'center',
								}}
							>
								<div style={{ padding: '12px 16px' }}>
									<div style={{ fontWeight: '500' }}>
										{queue.id
											? `Очередь №${queue.id} ${queue.name || ''}`
											: queue.name || 'Без названия'}
									</div>
									{queue.description && (
										<div
											style={{
												fontSize: '0.9em',
												color: '#666',
												marginTop: '4px',
											}}
										>
											{queue.description}
										</div>
									)}
								</div>
								<div style={{ padding: '12px 16px' }}>
									<span
										style={{
											display: 'inline-block',
											padding: '4px 8px',
											backgroundColor: '#e8f4fd',
											borderRadius: '4px',
											fontSize: '0.9em',
											color: '#0366d6',
										}}
									>
										{getConnectionName(queue.connectionId)}
									</span>
								</div>
								<div style={{ padding: '12px 16px' }}>
									<span
										style={{
											display: 'inline-flex',
											alignItems: 'center',
											color: isRunning ? '#28a745' : '#6c757d',
											fontWeight: '500',
											padding: '4px 12px',
											borderRadius: '20px',
											backgroundColor: isRunning ? '#e8f5e8' : '#f8f9fa',
											fontSize: '0.9em',
										}}
									>
										{isRunning ? (
											<>
												<span
													style={{
														display: 'inline-block',
														width: '8px',
														height: '8px',
														borderRadius: '50%',
														backgroundColor: '#28a745',
														marginRight: '6px',
														animation: 'pulse 1.5s infinite',
													}}
												></span>
												Запускается...
											</>
										) : (
											'Остановлена'
										)}
									</span>
								</div>
								<div style={{ padding: '12px 16px' }}>
									<div
										style={{
											display: 'flex',
											gap: '8px',
											flexWrap: 'wrap',
										}}
									>
										<button
											className='button button-small'
											onClick={() => handleRunQueue(queue.id)}
											disabled={isRunning}
											style={{
												padding: '6px 12px',
												fontSize: '0.85em',
											}}
										>
											{isRunning ? 'Запускается...' : 'Запустить'}
										</button>
										<button
											className='button button-small'
											onClick={() => handleEditQueue(queue)}
											style={{
												padding: '6px 12px',
												fontSize: '0.85em',
											}}
										>
											Редактировать
										</button>
										<Link to={`/queues/${queue.id}`}>
											<button
												className='button button-small'
												style={{
													padding: '6px 12px',
													fontSize: '0.85em',
												}}
											>
												Запросы
											</button>
										</Link>
										<button
											className='button button-danger button-small'
											onClick={() => handleDeleteQueue(queue.id)}
											style={{
												padding: '6px 12px',
												fontSize: '0.85em',
											}}
										>
											Удалить
										</button>
									</div>
								</div>
							</div>
						)
					})
				)}
			</div>

			<div className='add-section' style={{ marginTop: '24px' }}>
				<button className='button' onClick={handleAddQueue}>
					Добавить очередь
				</button>
			</div>

			<Modal
				isOpen={showModal}
				onClose={() => {
					setShowModal(false)
					setEditingQueue(null)
				}}
				title={editingQueue ? 'Редактирование очереди' : 'Добавление очереди'}
			>
				<form onSubmit={handleSaveQueue}>
					<div className='form-group'>
						<label>Название очереди (таблицы в витрине):</label>
						<input
							type='text'
							value={queueForm.name}
							onChange={e =>
								setQueueForm({ ...queueForm, name: e.target.value })
							}
							placeholder='datamart_1'
							required
						/>
					</div>

					<div className='form-group'>
						<label>Подключение:</label>
						<select
							value={queueForm.connectionId}
							onChange={e =>
								setQueueForm({ ...queueForm, connectionId: e.target.value })
							}
							required
						>
							<option value=''>Выберите подключение</option>
							{connections.map(conn => (
								<option key={conn.id} value={conn.id}>
									{conn.name} ({conn.db_type})
								</option>
							))}
						</select>
					</div>

					<div className='form-group'>
						<label>Описание:</label>
						<textarea
							rows='3'
							value={queueForm.description}
							onChange={e =>
								setQueueForm({ ...queueForm, description: e.target.value })
							}
							placeholder='Описание очереди ETL'
						/>
					</div>

					<div className='form-group'>
						<label
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '10px',
								cursor: 'pointer',
							}}
						>
							<input
								type='checkbox'
								checked={queueForm.systemResult}
								onChange={e =>
									setQueueForm({ ...queueForm, systemResult: e.target.checked })
								}
							/>
							<div>
								<div style={{ fontWeight: '500' }}>Системный результат</div>
								<div style={{ fontSize: '0.9em', color: '#666' }}>
									Сохранять результаты выполнения в системных логах
								</div>
							</div>
						</label>
					</div>

					<div className='form-actions'>
						<button
							type='button'
							className='button button-secondary'
							onClick={() => setShowModal(false)}
						>
							Отмена
						</button>
						<button type='submit' className='button'>
							{editingQueue ? 'Сохранить' : 'Добавить'}
						</button>
					</div>
				</form>
			</Modal>

			<style jsx>{`
				@keyframes pulse {
					0% {
						opacity: 1;
					}
					50% {
						opacity: 0.5;
					}
					100% {
						opacity: 1;
					}
				}
			`}</style>
		</div>
	)
}

export default Queues
